import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import TemplateCard from "@/components/resume-gallery/TemplateCard";
import TemplatePreviewModal from "@/components/resume-gallery/TemplatePreviewModal";
import { GALLERY_TEMPLATES } from "@/data/galleryTemplates";
import { COLOR_THEMES } from "@/components/resume-builder/templates/templateConfig";

const STYLE_CHIPS = ["Modern", "Classic", "Creative", "ATS-Friendly"];
const LEVEL_CHIPS = ["Entry", "Mid", "Senior", "Architect"];
const TRACK_CHIPS = [
  { id: "data_cloud", label: "Data Cloud" },
  { id: "sfmc", label: "SFMC" },
  { id: "mc_next", label: "MC Next" },
];

function ChipRow({ label, options, active, onToggle }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold text-slate-500 w-24 shrink-0">{label}</span>
      {options.map((opt) => {
        const val = opt.id || opt;
        const display = opt.label || opt;
        const isActive = active.has(val);
        return (
          <button
            key={val} onClick={() => onToggle(val)} data-testid={`gallery-chip-${label.toLowerCase().replace(" ", "-")}-${val}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${isActive ? "bg-indigo-500 border-indigo-500 text-white" : "border-white/10 text-slate-400 hover:text-white hover:border-white/30"}`}
          >
            {display}
          </button>
        );
      })}
    </div>
  );
}

export default function ResumeTemplatesGalleryPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [styleFilter, setStyleFilter] = useState(new Set());
  const [levelFilter, setLevelFilter] = useState(new Set());
  const [trackFilter, setTrackFilter] = useState(new Set());
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const toggle = (set, setter, val) => {
    const next = new Set(set);
    next.has(val) ? next.delete(val) : next.add(val);
    setter(next);
  };

  const filtered = useMemo(() => {
    return GALLERY_TEMPLATES.filter((t) => {
      const q = search.trim().toLowerCase();
      if (q && !t.name.toLowerCase().includes(q) && !t.style.toLowerCase().includes(q)) return false;
      if (styleFilter.size > 0 && !styleFilter.has(t.style)) return false;
      if (levelFilter.size > 0 && !t.roleLevel.some((l) => levelFilter.has(l))) return false;
      if (trackFilter.size > 0 && !t.tracks.some((tr) => trackFilter.has(tr))) return false;
      return true;
    });
  }, [search, styleFilter, levelFilter, trackFilter]);

  const applyTemplate = (template, colorId) => {
    navigate("/resume-builder", { state: { presetTemplate: template.id, presetColor: colorId || template.colorId } });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="resume-templates-gallery-page">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Job Ready Learning</span>
      <h1 className="font-heading text-3xl font-semibold text-white mt-1 mb-1">Resume Templates</h1>
      <p className="text-slate-400 text-sm mb-6">{GALLERY_TEMPLATES.length * COLOR_THEMES.length} real, working looks for Salesforce professionals — search, filter, and start editing instantly.</p>

      <div className="relative mb-5">
        <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search resume templates..."
          data-testid="gallery-search-input"
          className="w-full bg-[#12141c] border border-white/10 rounded-full pl-10 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-400"
        />
      </div>

      <div className="space-y-2.5 mb-7">
        <ChipRow label="Style" options={STYLE_CHIPS} active={styleFilter} onToggle={(v) => toggle(styleFilter, setStyleFilter, v)} />
        <ChipRow label="Role Level" options={LEVEL_CHIPS} active={levelFilter} onToggle={(v) => toggle(levelFilter, setLevelFilter, v)} />
        <ChipRow label="Track" options={TRACK_CHIPS} active={trackFilter} onToggle={(v) => toggle(trackFilter, setTrackFilter, v)} />
      </div>

      {filtered.length === 0 ? (
        <p className="text-center text-slate-400 py-16" data-testid="gallery-no-results">No templates match your filters — try clearing some.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="gallery-template-grid">
          {filtered.map((t) => (
            <TemplateCard key={t.id} template={t} onPreview={setPreviewTemplate} onUse={(tpl) => applyTemplate(tpl)} />
          ))}
        </div>
      )}

      <TemplatePreviewModal template={previewTemplate} open={!!previewTemplate} onClose={() => setPreviewTemplate(null)} onUse={applyTemplate} />
    </div>
  );
}
